import { Block, Document, type Schema, Text, Value } from '@gitbook/slate';

import { schemaForConflicts, schemaToStripConflicts } from './merge';
import { SCHEMA_VERSION, schema, schemaBasic, schemaForReusableContent } from './schema';

export type DocumentSchemaType = 'document' | 'conflicts' | 'basic' | 'reusable-content';

export const SCHEMAS: { [key in DocumentSchemaType]: Schema } = {
    document: schema.combineWith(schemaToStripConflicts),
    conflicts: schema.combineWith(schemaForConflicts),
    basic: schemaBasic,
    'reusable-content': schemaForReusableContent,

    // Reusable content used to be called synced-blocks, keep this around for legacy reasons
    // @ts-expect-error
    'synced-block': schemaForReusableContent,
};

/**
 * Normalize a document
 */
export function normalizeDocument(
    document: Document,
    schemaToUse: DocumentSchemaType = 'document'
): Document {
    return Value.create({ document, schema: SCHEMAS[schemaToUse] }).change().normalizeDocument()
        .value.document;
}

/**
 * Normalize a document to upgrade it to the latest schema
 */
export function normalizeDocumentToLatestSchema(
    document: Document,
    schemaToUse: DocumentSchemaType = 'document'
): Document {
    // createEditorValue does the normalization.
    const value = createEditorValue(document, schemaToUse);
    return value.document;
}

/**
 * Create a value for a document with a schema
 */
export function createEditorValue(
    input: EditorDocumentInput = '',
    schemaToUse: DocumentSchemaType = 'document'
): Value {
    let document = createEditorDocument(input);

    // For non-default schemas, we need to set the schema type.
    if (schemaToUse !== 'document') {
        document = document.setData(['schemaType'], schemaToUse);
    }

    return Value.create(
        {
            document,
            schema: SCHEMAS[schemaToUse],
        },
        {
            normalize:
                schemaToUse !== 'document' ||
                // Normalize the document only if it was generated by a different schema
                document.data.get('schemaVersion', 0) !== SCHEMA_VERSION,
        }
    );
}

const lazyEmptyEditorValue: { [key in string]?: Value } = {};

/**
 * Get an empty initial value for a schema.
 * Values are cached based on the provided key. Multiple calls with the same key will return the same instance.
 * You should provide an appropriate key: Slate does not support two Values with the same key being rendered on the page the same time.
 */
export function getEmptyEditorValue(
    key: string,
    schemaToUse: DocumentSchemaType = 'document'
): Value {
    if (lazyEmptyEditorValue[key]) {
        return lazyEmptyEditorValue[key];
    }
    lazyEmptyEditorValue[key] = createEditorValue('', schemaToUse);
    return lazyEmptyEditorValue[key];
}

/**
 * Normalize a value
 */
export function normalizeValue(value: Value, schemaToUse: DocumentSchemaType = 'document'): Value {
    return createEditorValue(value, schemaToUse).change().normalizeDocument().value;
}

type EditorDocumentInput = Value | Document | string | { nodes: Block[] };

/**
 * Create an editor document.
 */
export function createEditorDocument(input: EditorDocumentInput): Document {
    if (typeof input === 'string') {
        return Document.create({
            nodes: [
                Block.create({
                    type: 'paragraph',
                    nodes: [Text.create(input)],
                }),
            ],
        });
    }
    if (Document.isDocument(input)) {
        return input;
    }
    if (Value.isValue(input)) {
        return input.document;
    }
    return Document.create(input);
}

/**
 * Create an initial editor value for a page sheet with predictable keys.
 * Predicable keys are important for RTC and comments.
 * You must also provide a document schema, which will be used to normalize and will be persisted with the document.
 */
export function createSheetEditorValue(pageKey: string, schemaToUse: DocumentSchemaType): Value {
    return createEditorValue(
        {
            nodes: [
                Block.create({
                    key: `initial_${pageKey}_block`,
                    type: 'paragraph',
                    nodes: [
                        Text.create({
                            key: `initial_${pageKey}_text`,
                            text: '',
                        }),
                    ],
                }),
            ],
        },
        schemaToUse
    );
}
